// ═══════════════════════════════════════════════════════════════
// PLAID LINK — the shared half, because OAuth banks leave the page
// ═══════════════════════════════════════════════════════════════
// Most banks let Plaid Link finish inside its own modal: pick the bank, type
// the password in Plaid's window, onSuccess fires, done. The big ones do not.
// Chase, Capital One, Wells Fargo, US Bank, PNC, Amex and most large issuers
// are OAuth on Plaid, which means Link REDIRECTS the whole browser to the
// bank's own site. MOS unloads. Whatever the connect button was holding in
// component state is gone.
//
// The bank sends the browser back to a URI Plaid has on file for us, and Link
// resumes only if we re-create it with the SAME link_token plus the URL we
// landed on (receivedRedirectUri). So the token has to outlive the page: it
// goes to localStorage on the way out and is read back on the way in. That
// hand-off, and the exchange both halves perform on success, live here so the
// panel and the return screen cannot drift apart.
//
// Nothing here is needed for a non-OAuth bank. It is also harmless there —
// the stash is written, unused, and cleared on success.

import { projectId } from './supabase/info';
import { bearerHeader } from './apiAuth';

export const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e3a97ae5`;

/** The path Plaid sends OAuth users back to. Registered in the Plaid dashboard
 *  and handed to /link/token/create as an absolute URI — the two must match
 *  exactly, so both sides derive it from this one constant. */
export const PLAID_OAUTH_PATH = '/plaid-oauth';

const PLAID_LINK_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
const HANDOFF_KEY = 'mos_plaid_oauth_handoff_v1';

export interface PlaidLinkMetadata { institution?: { name?: string } }

declare global {
  interface Window {
    Plaid?: {
      create: (cfg: {
        token: string;
        /** Present only on the OAuth return: the full URL the bank sent us to,
         *  query string included. Link reads its state out of it. */
        receivedRedirectUri?: string;
        onSuccess: (publicToken: string, metadata: PlaidLinkMetadata) => void;
        onExit: (err: unknown) => void;
      }) => { open: () => void };
    };
  }
}

let plaidScriptPromise: Promise<void> | null = null;

export function loadPlaidLink(): Promise<void> {
  if (window.Plaid) return Promise.resolve();
  if (!plaidScriptPromise) {
    plaidScriptPromise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = PLAID_LINK_SRC;
      el.onload = () => resolve();
      el.onerror = () => { plaidScriptPromise = null; reject(new Error('Could not load Plaid')); };
      document.head.appendChild(el);
    });
  }
  return plaidScriptPromise;
}

// ── The hand-off across the redirect ───────────────────────────────────────

interface Handoff {
  linkToken: string;
  /** Set when this was a reconnect, so the return finishes in update mode and
   *  does not register the bank a second time. */
  itemId: string | null;
  /** Plaid's own expiry for the token. Past it, Link cannot resume and the
   *  honest move is to say so rather than open a window that will fail. */
  expiresAt: string | null;
}

export function stashLinkHandoff(h: Handoff): void {
  try { localStorage.setItem(HANDOFF_KEY, JSON.stringify(h)); } catch { /* private mode */ }
}

export function clearLinkHandoff(): void {
  try { localStorage.removeItem(HANDOFF_KEY); } catch { /* ignore */ }
}

export function readLinkHandoff(): Handoff | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(HANDOFF_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const h = JSON.parse(raw) as Partial<Handoff>;
    if (typeof h.linkToken !== 'string' || !h.linkToken) return null;
    if (h.expiresAt && Date.parse(h.expiresAt) < Date.now()) return null;
    return {
      linkToken: h.linkToken,
      itemId: typeof h.itemId === 'string' ? h.itemId : null,
      expiresAt: typeof h.expiresAt === 'string' ? h.expiresAt : null,
    };
  } catch {
    return null;
  }
}

// ── What both halves do when Link succeeds ─────────────────────────────────

export interface ExchangeResult { institution: string; accounts: number }

/** Trade the one-time public token for a stored connection. The access token
 *  never comes back over the wire — it goes straight into the Vault server
 *  side. A reconnect has nothing to exchange: the item already exists and
 *  update mode has just repaired it in place. */
export async function exchangePublicToken(
  publicToken: string,
  institution: string,
): Promise<ExchangeResult> {
  const res = await fetch(`${API_BASE}/money/plaid/exchange`, {
    method: 'POST',
    headers: { Authorization: bearerHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ public_token: publicToken, institution }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return { institution: String(json.institution ?? institution), accounts: Number(json.accounts ?? 0) };
}

/** Ask for a link token. Passing an item id puts Link in update mode, which
 *  re-authorizes THAT connection rather than adding a second one. */
export async function createLinkToken(
  itemId?: string | null,
): Promise<{ linkToken: string; expiresAt: string | null }> {
  const res = await fetch(`${API_BASE}/money/plaid/link-token`, {
    method: 'POST',
    headers: { Authorization: bearerHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(itemId ? { item_id: itemId } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return {
    linkToken: String(json.link_token ?? ''),
    expiresAt: json.expiration ? String(json.expiration) : null,
  };
}
